import { useEffect, useState, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { 
  Play, 
  X, 
  Check, 
  Phone, 
  Mail, 
  MessageCircle,
  Target,
  Clapperboard,
  MonitorPlay,
  Send,
  Shield,
  Users,
  Zap,
  Award,
  Loader2,
  ChevronDown
} from "lucide-react";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo components
const LogoWhite = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_production-hub-pro/artifacts/dtengl6e_Risorsa%202%20%282%29.png" 
    alt="DoooIt" 
    className="h-8 md:h-10"
  />
);

const LogoBlack = () => (
  <img 
    src="https://customer-assets.emergentagent.com/job_production-hub-pro/artifacts/9xielqu1_Risorsa%201%20%283%29.png" 
    alt="DoooIt" 
    className="h-8 md:h-10"
  />
);

// Noise Overlay Component
const NoiseOverlay = () => (
  <div className="noise-overlay" aria-hidden="true" />
);

// Section Component
const Section = ({ children, className = "", id }) => (
  <section id={id} className={`section-padding ${className}`}>
    <div className="container-max">
      {children}
    </div>
  </section>
);

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Hero Section
const HeroSection = ({ onOpenForm, scrollToShowreel }) => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <video
        autoPlay
        muted
        loop
        playsInline
        className="hero-video"
        poster="https://images.pexels.com/photos/2873486/pexels-photo-2873486.jpeg?auto=compress&cs=tinysrgb&w=1920"
      >
        <source 
          src="https://cdn.coverr.co/videos/coverr-film-set-production-4766/1080p.mp4" 
          type="video/mp4" 
        />
      </video>
      
      {/* Overlay */}
      <div className="video-overlay" />
      
      {/* Content */}
      <div className="hero-content px-6 md:px-12 lg:px-24 w-full max-w-7xl">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl"
        >
          <motion.div variants={fadeInUp} className="mb-8">
            <LogoWhite />
          </motion.div>
          
          <motion.h1 
            variants={fadeInUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6"
          >
            Video o Foto per la tua azienda?<br />
            Hai appena trovato<br />
            <span className="text-white/60">il partner definitivo.</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-lg md:text-xl text-white/70 max-w-2xl mb-10"
          >
            Supportiamo aziende e agenzie di comunicazione nella realizzazione 
            di contenuti, con metodo, organizzazione e alti standard qualitativi.
          </motion.p>
          
          <motion.div 
            variants={fadeInUp}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button 
              onClick={onOpenForm}
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              Richiedi una call conoscitiva
            </button>
            <button 
              onClick={scrollToShowreel}
              className="btn-secondary flex items-center justify-center gap-2"
              data-testid="hero-cta-secondary"
            >
              <Play size={18} />
              Guarda alcuni nostri lavori
            </button>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <ChevronDown className="w-6 h-6 text-white/50" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Problem Section
const ProblemSection = () => {
  return (
    <Section className="bg-[#050505]" id="problem">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p 
          variants={fadeInUp}
          className="step-number mb-4"
        >
          IL PROBLEMA
        </motion.p>
        
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-8"
        >
          Sempre più aziende hanno bisogno di contenuti, ma...
        </motion.h2>
        
        <div className="grid md:grid-cols-2 gap-12 mt-12">
          <motion.div variants={fadeInUp}>
            <ul className="space-y-6 text-lg text-white/70">
              <li className="flex items-start gap-4">
                <span className="w-2 h-2 rounded-full bg-white/40 mt-3 flex-shrink-0" />
                <span>Molte aziende non hanno un reparto di produzione interno.</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-2 h-2 rounded-full bg-white/40 mt-3 flex-shrink-0" />
                <span>Molte case di produzione lavorano in modo poco organizzato.</span>
              </li>
              <li className="flex items-start gap-4">
                <span className="w-2 h-2 rounded-full bg-white/40 mt-3 flex-shrink-0" />
                <span>Molti progetti si rallentano per mancanza di metodo e organizzazione.</span>
              </li>
            </ul>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <p className="text-lg text-white/70 mb-6">Le aziende hanno bisogno di:</p>
            <ul className="space-y-4">
              {['Affidabilità', 'Qualità costante', 'Organizzazione chiara', 'Un partner che lavori al loro fianco'].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-white font-medium">
                  <Check className="w-5 h-5 text-white/60" />
                  {item}
                </li>
              ))}
            </ul>
            
            <p className="mt-10 text-2xl font-semibold">
              DoooIt nasce per questo.
            </p>
          </motion.div>
        </div>
      </motion.div>
    </Section>
  );
};

// Solution Section
const SolutionSection = () => {
  const solutions = [
    { 
      icon: Clapperboard, 
      title: "Esecutore operativo",
      desc: "Realizziamo i contenuti seguendo le tue indicazioni creative."
    },
    { 
      icon: Target, 
      title: "Supporto creativo",
      desc: "Proponiamo concept e soluzioni creative per arricchire i tuoi progetti."
    },
    { 
      icon: Users, 
      title: "Supporto strategico",
      desc: "Ti affianchiamo nella definizione della content strategy."
    },
  ];

  return (
    <Section className="bg-[#0a0a0a]" id="solution">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p 
          variants={fadeInUp}
          className="step-number mb-4"
        >
          LA SOLUZIONE
        </motion.p>
        
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
        >
          Un partner, non un fornitore.
        </motion.h2>
        
        <motion.p 
          variants={fadeInUp}
          className="text-lg md:text-xl text-white/60 max-w-3xl mb-12"
        >
          Un reparto produzione pronto a lavorare per la tua azienda, 
          con la massima flessibilità e professionalità.
        </motion.p>
        
        <motion.div 
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-6"
        >
          {solutions.map((item, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="card hover-lift"
            >
              <item.icon className="w-10 h-10 mb-6 text-white/80" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
              <p className="text-white/60">{item.desc}</p>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          variants={fadeInUp}
          className="mt-12 p-8 border border-white/10 bg-white/5"
        >
          <p className="text-center text-lg text-white/80">
            <span className="font-semibold text-white">Flessibilità totale</span> · 
            Partnership, non semplice fornitura · 
            Massima trasparenza · 
            Qualità garantita
          </p>
        </motion.div>
      </motion.div>
    </Section>
  );
};

// Method Section
const MethodSection = () => {
  const steps = [
    {
      num: "01",
      title: "Analisi strategica e pre-produzione",
      desc: "Definiamo obiettivi, target e formato dei contenuti insieme a te.",
      icon: Target
    },
    {
      num: "02",
      title: "Definizione content strategy",
      desc: "Pianifichiamo la produzione con timeline, risorse e deliverable chiari.",
      icon: Clapperboard
    },
    {
      num: "03",
      title: "Shooting organizzato",
      desc: "Produciamo con metodo: brief precisi, set coordinati, qualità costante.",
      icon: MonitorPlay
    },
    {
      num: "04",
      title: "Post-produzione controllata",
      desc: "Editing professionale con processo di revisione strutturato.",
      icon: Send
    }
  ];

  return (
    <Section className="bg-[#050505]" id="method">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p 
          variants={fadeInUp}
          className="step-number mb-4"
        >
          IL NOSTRO METODO
        </motion.p>
        
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4"
        >
          Metodo prima di tutto.
        </motion.h2>
        
        <motion.p 
          variants={fadeInUp}
          className="text-lg md:text-xl text-white/60 max-w-3xl mb-4"
        >
          Il settore produzione è spesso disorganizzato e legato all'impostazione 
          artistica degli operatori. DoooIt ha proceduralizzato ogni fase.
        </motion.p>
        
        <motion.p 
          variants={fadeInUp}
          className="text-xl font-semibold mb-12"
        >
          "Creatività sì. Disorganizzazione no."
        </motion.p>
        
        <motion.div 
          variants={staggerContainer}
          className="method-grid"
        >
          {steps.map((step, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="card hover-lift group"
            >
              <span className="step-number block mb-4">STEP {step.num}</span>
              <step.icon className="w-8 h-8 mb-4 text-white/60 group-hover:text-white transition-colors" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold mb-3">{step.title}</h3>
              <p className="text-sm text-white/60">{step.desc}</p>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          variants={fadeInUp}
          className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-4 text-center"
        >
          {[
            "Coordinamento centralizzato",
            "Team trasversale",
            "Flusso strutturato",
            "Consegne chiare",
            "Qualità costante"
          ].map((item, i) => (
            <div key={i} className="p-4 border border-white/10 text-sm text-white/70">
              {item}
            </div>
          ))}
        </motion.div>
      </motion.div>
    </Section>
  );
};

// Video Modal Component
const VideoModal = ({ isOpen, onClose, videoId, title }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center p-4"
          onClick={onClose}
        >
          {/* Close button - top right, always visible */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 md:top-8 md:right-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 transition-all border border-white/20 z-[70]"
            data-testid="close-video-modal"
          >
            <X size={20} />
            <span className="text-sm font-medium">Chiudi</span>
          </button>
          
          {/* Back button - top left */}
          <button 
            onClick={onClose}
            className="absolute top-4 left-4 md:top-8 md:left-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 transition-all border border-white/20 z-[70]"
            data-testid="back-button-video-modal"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span className="text-sm font-medium">Indietro</span>
          </button>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-md mt-16"
            onClick={(e) => e.stopPropagation()}
          >
            <div 
              className="bg-black border border-white/20 rounded-sm overflow-hidden"
              style={{ aspectRatio: '9/16' }}
            >
              <iframe
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                title={title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
                style={{ border: 'none' }}
              />
            </div>
            
            <p className="text-center mt-4 text-white/60 text-sm">{title}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Showreel Section
const ShowreelSection = () => {
  const [activeVideo, setActiveVideo] = useState(null);
  
  const mainShowreels = [
    { id: "ovynTj63Hic", title: "Showreel 1" },
    { id: "BQmfurDV01k", title: "Showreel 2" },
  ];

  const sectors = [
    { name: "Automotive", thumb: "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?auto=format&fit=crop&w=800&q=80", videoId: "X_NawMW5q1g" },
    { name: "Retail", thumb: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=800&q=80", videoId: "pKeJ1MTKIBo" },
    { name: "Eventi", thumb: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=800&q=80", videoId: "NtOhx2nLo_k" },
    { name: "Hotellerie", thumb: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=800&q=80", videoId: "UAatz4XY840" },
    { name: "Food & Beverage", thumb: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&q=80", videoId: "ODIWvog1sqE" },
    { name: "Medical & Beauty", thumb: "https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?auto=format&fit=crop&w=800&q=80", videoId: "AT19znk3MVQ" },
    { name: "Fashion", thumb: "https://images.unsplash.com/photo-1509631179647-0177331693ae?auto=format&fit=crop&w=800&q=80", videoId: "i6gqu6F8gdM" },
  ];

  // Split sectors into two rows: 4 on top, 3 on bottom
  const topRow = sectors.slice(0, 4);
  const bottomRow = sectors.slice(4);

  return (
    <Section className="bg-[#0a0a0a]" id="showreel">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p 
          variants={fadeInUp}
          className="step-number mb-4"
        >
          PORTFOLIO
        </motion.p>
        
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12"
        >
          I nostri lavori
        </motion.h2>
        
        {/* Main Showreels - Vertical Videos */}
        <motion.div 
          variants={fadeInUp}
          className="mb-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {mainShowreels.map((reel, i) => (
              <div 
                key={i}
                className="relative bg-neutral-900 border border-white/10 overflow-hidden rounded-sm"
                style={{ aspectRatio: '9/16', maxHeight: '70vh' }}
              >
                <iframe
                  src={`https://www.youtube.com/embed/${reel.id}?autoplay=0&rel=0&modestbranding=1`}
                  title={reel.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full"
                  style={{ border: 'none' }}
                />
              </div>
            ))}
          </div>
        </motion.div>
        
        {/* Sectors */}
        <motion.p 
          variants={fadeInUp}
          className="text-lg font-semibold mb-6"
        >
          Alcuni dei settori in cui operiamo
        </motion.p>
        
        {/* Top Row - 4 items */}
        <motion.div 
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-4"
        >
          {topRow.map((sector, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="video-thumb aspect-square bg-neutral-900 border border-white/10 overflow-hidden group cursor-pointer hover-lift"
              onClick={() => setActiveVideo(sector)}
              data-testid={`sector-${sector.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <img 
                src={sector.thumb}
                alt={sector.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="play-overlay">
                <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform">
                  <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                </div>
              </div>
              <span className="sector-badge">{sector.name}</span>
            </motion.div>
          ))}
        </motion.div>
        
        {/* Bottom Row - 3 items centered on desktop, stacked on mobile */}
        <motion.div 
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 md:flex md:justify-center gap-4"
        >
          {bottomRow.map((sector, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="video-thumb aspect-square bg-neutral-900 border border-white/10 overflow-hidden group cursor-pointer hover-lift md:w-[calc(25%-0.75rem)]"
              onClick={() => setActiveVideo(sector)}
              data-testid={`sector-${sector.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <img 
                src={sector.thumb}
                alt={sector.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="play-overlay">
                <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur flex items-center justify-center border border-white/30 group-hover:scale-110 transition-transform">
                  <Play className="w-5 h-5 text-white ml-0.5" fill="white" />
                </div>
              </div>
              <span className="sector-badge">{sector.name}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
      
      {/* Video Modal */}
      <VideoModal 
        isOpen={!!activeVideo}
        onClose={() => setActiveVideo(null)}
        videoId={activeVideo?.videoId}
        title={activeVideo?.name}
      />
    </Section>
  );
};

// White Label Section
const WhiteLabelSection = () => {
  return (
    <Section className="bg-[#050505]" id="whitelabel">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="text-center max-w-4xl mx-auto"
      >
        <motion.div variants={fadeInUp}>
          <Shield className="w-16 h-16 mx-auto mb-8 text-white/60" strokeWidth={1} />
        </motion.div>
        
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
        >
          Trasparenza e affidabilità.
        </motion.h2>
        
        <motion.p 
          variants={fadeInUp}
          className="text-lg md:text-xl text-white/60 mb-12"
        >
          Lavoriamo in totale trasparenza, al tuo fianco. 
          Il nostro obiettivo è supportarti in ogni fase del progetto.
        </motion.p>
        
        <motion.div 
          variants={staggerContainer}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
        >
          {[
            "Comunicazione costante",
            "Rispetto delle deadline",
            "Massima riservatezza",
            "Qualità garantita"
          ].map((item, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="p-6 border border-white/10 bg-white/5"
            >
              <Check className="w-5 h-5 mb-3 mx-auto text-white/60" />
              <p className="text-sm">{item}</p>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.p 
          variants={fadeInUp}
          className="mt-10 text-white/40 text-sm"
        >
          Il tuo successo è il nostro successo.
        </motion.p>
      </motion.div>
    </Section>
  );
};

// Benefits Section
const BenefitsSection = () => {
  const benefits = [
    "Organizzazione proceduralizzata",
    "Team completo interno",
    "Metodo strutturato",
    "Qualità dell'output elevata",
    "Massima flessibilità operativa",
    "Comunicazione trasparente",
    "Supporto modulare (esecutivo, creativo, strategico)"
  ];

  return (
    <Section className="bg-[#0a0a0a]" id="benefits">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
      >
        <motion.p 
          variants={fadeInUp}
          className="step-number mb-4"
        >
          PERCHÉ SCEGLIERE DOOOIT
        </motion.p>
        
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-12"
        >
          I vantaggi di lavorare con noi
        </motion.h2>
        
        <motion.div 
          variants={staggerContainer}
          className="grid md:grid-cols-2 gap-x-12 gap-y-2 max-w-4xl"
        >
          {benefits.map((item, i) => (
            <motion.div 
              key={i}
              variants={fadeInUp}
              className="check-item border-b border-white/10"
            >
              <Check className="check-icon" />
              <span className="text-lg">{item}</span>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </Section>
  );
};

// Final CTA Section
const FinalCTASection = ({ onOpenForm }) => {
  return (
    <Section className="bg-[#050505] py-24 md:py-32" id="cta">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-100px" }}
        variants={staggerContainer}
        className="text-center max-w-3xl mx-auto"
      >
        <motion.h2 
          variants={fadeInUp}
          className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6"
        >
          Pronto a portare i tuoi contenuti al livello successivo?
        </motion.h2>
        
        <motion.p 
          variants={fadeInUp}
          className="text-xl text-white/60 mb-10"
        >
          Parliamone.<br />
          Valutiamo insieme come possiamo supportare la tua azienda nei prossimi progetti.
        </motion.p>
        
        <motion.div 
          variants={fadeInUp}
          className="flex flex-col items-center gap-4"
        >
          <button 
            onClick={onOpenForm}
            className="btn-primary text-lg px-10 py-4"
            data-testid="final-cta-button"
          >
            Richiedi ora una call conoscitiva
          </button>
          
          <div className="flex flex-col sm:flex-row items-center gap-3 mt-4">
            <a 
              href="tel:+393312877385" 
              className="btn-secondary flex items-center gap-2 px-6 py-3"
              data-testid="phone-button"
            >
              <Phone size={18} />
              331 287 7385
            </a>
            <a 
              href="https://wa.me/393312877385" 
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary flex items-center gap-2 px-6 py-3 hover:bg-[#25D366]/20 hover:border-[#25D366]/50"
              data-testid="whatsapp-button-cta"
            >
              <MessageCircle size={18} />
              WhatsApp
            </a>
          </div>
        </motion.div>
      </motion.div>
    </Section>
  );
};

// Lead Form Modal
const LeadFormModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    owner_name: "",
    agency_name: "",
    email: "",
    phone: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/leads`, formData);
      setIsSuccess(true);
      toast.success("Richiesta inviata con successo!");
    } catch (error) {
      console.error("Error submitting lead:", error);
      toast.error("Errore nell'invio. Riprova.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({ owner_name: "", agency_name: "", email: "", phone: "" });
    setIsSuccess(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="modal-backdrop flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="bg-[#0a0a0a] border border-white/10 w-full max-w-lg p-8 md:p-10 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors"
              data-testid="close-modal-button"
            >
              <X size={24} />
            </button>
            
            {!isSuccess ? (
              <>
                <div className="mb-8">
                  <LogoWhite />
                </div>
                
                <h3 className="text-2xl font-bold mb-2">
                  Richiedi una call conoscitiva
                </h3>
                <p className="text-white/60 mb-8">
                  Lascia i tuoi dati, ti ricontatteremo entro 24 ore.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="lead-form">
                  <div>
                    <input
                      type="text"
                      name="owner_name"
                      placeholder="Nome e Cognome *"
                      required
                      value={formData.owner_name}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-owner-name"
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      name="agency_name"
                      placeholder="Nome Azienda *"
                      required
                      value={formData.agency_name}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-agency-name"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email *"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Numero di telefono *"
                      required
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      data-testid="input-phone"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="submit-lead-button"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 spinner" />
                        Invio in corso...
                      </>
                    ) : (
                      "Invia richiesta"
                    )}
                  </button>
                </form>
              </>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-6">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold mb-3">
                  Richiesta inviata!
                </h3>
                <p className="text-white/60 mb-8">
                  Grazie per averci contattato.<br />
                  Ti ricontatteremo entro 24 ore lavorative.
                </p>
                <button
                  onClick={resetForm}
                  className="btn-secondary"
                  data-testid="close-success-button"
                >
                  Chiudi
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// WhatsApp Button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/393312877385"
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-btn"
    aria-label="Contattaci su WhatsApp"
    data-testid="whatsapp-button"
  >
    <MessageCircle size={24} fill="white" />
  </a>
);

// Footer
const Footer = () => (
  <footer className="bg-[#050505] border-t border-white/10 py-8 px-6">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
      <LogoWhite />
      <p className="text-white/40 text-sm">
        © {new Date().getFullYear()} DoooIt Video Production. Tutti i diritti riservati.
      </p>
      <div className="flex items-center gap-4">
        <a 
          href="mailto:marketing@doooit.studio" 
          className="text-white/40 hover:text-white transition-colors"
          title="Scrivici una email"
        >
          <Mail size={20} />
        </a>
        <a 
          href="tel:+393312877385" 
          className="text-white/40 hover:text-white transition-colors"
          title="Chiamaci"
        >
          <Phone size={20} />
        </a>
      </div>
    </div>
  </footer>
);

// Main App Component
function App() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const lenisRef = useRef(null);

  useEffect(() => {
    // Initialize Lenis smooth scroll
    lenisRef.current = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time) {
      lenisRef.current?.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenisRef.current?.destroy();
    };
  }, []);

  const scrollToShowreel = () => {
    const element = document.getElementById('showreel');
    if (element) {
      lenisRef.current?.scrollTo(element);
    }
  };

  return (
    <div className="App bg-[#050505] min-h-screen">
      <NoiseOverlay />
      <Toaster 
        position="top-center" 
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255,255,255,0.1)'
          }
        }}
      />
      
      <HeroSection 
        onOpenForm={() => setIsFormOpen(true)} 
        scrollToShowreel={scrollToShowreel}
      />
      <ProblemSection />
      <SolutionSection />
      <MethodSection />
      <ShowreelSection />
      <WhiteLabelSection />
      <BenefitsSection />
      <FinalCTASection onOpenForm={() => setIsFormOpen(true)} />
      <Footer />
      
      <WhatsAppButton />
      <LeadFormModal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} />
    </div>
  );
}

export default App;
